import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Table, TableHead, TableRow, TableCell, TableBody, Select, MenuItem, 
  Button, Paper, Box, Tabs, Tab, Chip, IconButton, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, Avatar, Stack, Pagination, InputAdornment, Grid, Card, CardContent,
  Divider, FormControl, InputLabel, DialogContentText, Switch, FormControlLabel, Alert, 
  CircularProgress, Tooltip, Snackbar, LinearProgress, Autocomplete
} from '@mui/material';

// FIREBASE IMPORTS
import { getAuth } from "firebase/auth";
import { 
    collection, getDocs, doc, deleteDoc, updateDoc, 
    setDoc, getDoc 
} from "firebase/firestore";
import { db } from '../firebase'; 

// CHARTS
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

// ICONS
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import FeedbackIcon from '@mui/icons-material/Feedback';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalOfferIcon from '@mui/icons-material/LocalOffer'; 
import ChefHatIcon from '@mui/icons-material/Cookie'; 
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CampaignIcon from '@mui/icons-material/Campaign'; 
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import { toTitleCase, SEED_INGREDIENTS } from '../utils/recipeStandardization';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#e91e63']; 
const CUISINE_COLORS = ['#477491', '#D28415', '#2e7d32', '#9c27b0', '#e91e63', '#0097a7'];

// Robust date parser for Firestore Timestamps, Date strings, or epoch numbers
const parseDate = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (typeof timestamp === 'object' && timestamp.seconds !== undefined) {
        return new Date(timestamp.seconds * 1000);
    }
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (timestamp) => {
    const date = parseDate(timestamp);
    if (!date) return "Never";
    return date.toLocaleString(undefined, { 
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

// Returns 1 or 2 uppercase letters for user avatars
const getInitials = (name = '') => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase() || '?';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  // TABS & LOADING
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // DATA STATES
  const [users, setUsers] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [tags, setTags] = useState([]); // Derived from recipes
  const [analytics, setAnalytics] = useState(null);

  // FEEDBACK NOTIFICATIONS (SNACKBAR)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // PAGINATION & FILTER STATES
  const ITEMS_PER_PAGE = 10;

  // Users tab
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");

  // Feedback tab
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState("all");
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState("all");

  // Recipes tab
  const [recipePage, setRecipePage] = useState(1);
  const [recipeSearch, setRecipeSearch] = useState("");
  const [recipeCuisineFilter, setRecipeCuisineFilter] = useState("all");
  const [recipeFeaturedFilter, setRecipeFeaturedFilter] = useState("all");

  // Tags tab
  const [tagSearch, setTagSearch] = useState("");

  // DIALOGS
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");

  // Tag Merge Dialog
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [tagToMerge, setTagToMerge] = useState(null);
  const [targetTag, setTargetTag] = useState("");

  // Tag Rename Dialog
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [tagToRename, setTagToRename] = useState(null);
  const [newTagName, setNewTagName] = useState("");

  // SETTINGS
  const [bannerMsg, setBannerMsg] = useState("");
  const [bannerType, setBannerType] = useState("info");
  const [bannerActive, setBannerActive] = useState(false);
  const [bannerSaved, setBannerSaved] = useState(false);

  // DICTIONARY / AUTOCOMPLETE TAB
  const [dictionaryIngredients, setDictionaryIngredients] = useState([]);
  const [dictionaryCuisines, setDictionaryCuisines] = useState([]);
  const [dictSearch, setDictSearch] = useState("");
  const [dictPage, setDictPage] = useState(1);
  const DICT_ITEMS_PER_PAGE = 25;

  const [addIngOpen, setAddIngOpen] = useState(false);
  const [newIngName, setNewIngName] = useState("");
  const [editIngOpen, setEditIngOpen] = useState(false);
  const [ingToEdit, setIngToEdit] = useState(null);
  const [editedIngName, setEditedIngName] = useState("");
  const [deleteIngOpen, setDeleteIngOpen] = useState(false);
  const [ingToDelete, setIngToDelete] = useState(null);

  const [addCuisineOpen, setAddCuisineOpen] = useState(false);
  const [newCuisineName, setNewCuisineName] = useState("");
  const [editCuisineOpen, setEditCuisineOpen] = useState(false);
  const [cuisineToEdit, setCuisineToEdit] = useState(null);
  const [editedCuisineName, setEditedCuisineName] = useState("");
  const [deleteCuisineOpen, setDeleteCuisineOpen] = useState(false);
  const [cuisineToDelete, setCuisineToDelete] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Merge Ingredient & Cuisine States
  const [mergeIngOpen, setMergeIngOpen] = useState(false);
  const [ingToMerge, setIngToMerge] = useState(null);
  const [targetIng, setTargetIng] = useState("");
  const [isMergingIng, setIsMergingIng] = useState(false);

  const [mergeCuisineOpen, setMergeCuisineOpen] = useState(false);
  const [cuisineToMerge, setCuisineToMerge] = useState(null);
  const [targetCuisine, setTargetCuisine] = useState("");
  const [isMergingCuisine, setIsMergingCuisine] = useState(false);

  // --- 1. INITIALIZATION & AUTH CHECK ---
  useEffect(() => {
    const checkAdmin = async () => {
        const user = auth.currentUser;
        if (!user) {
            navigate('/login');
            return;
        }

        try {
            const userDoc = await getDoc(doc(db, "users", user.uid));
            if (userDoc.exists() && userDoc.data().role === 'admin') {
                setIsAdmin(true);
                fetchAllData();
            } else {
                navigate('/recipes');
            }
        } catch (error) {
            console.error("Auth Error:", error);
            navigate('/login');
        }
    };
    checkAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, navigate]);

  // --- 2. DATA FETCHING (Firestore) ---
  const fetchAllData = async () => {
      setIsLoading(true);
      try {
          // A. Fetch Users
          const usersSnap = await getDocs(collection(db, "users"));
          const usersList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setUsers(usersList);

          // B. Fetch Recipes
          const recipesSnap = await getDocs(collection(db, "recipes"));
          const recipesList = recipesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setRecipes(recipesList);

          // C. Fetch Feedback
          const feedbackSnap = await getDocs(collection(db, "feedback"));
          const feedbackList = feedbackSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setFeedback(feedbackList);

          // D. Fetch Settings
          const settingsSnap = await getDoc(doc(db, "settings", "global"));
          if (settingsSnap.exists()) {
              const data = settingsSnap.data();
              setBannerMsg(data.announcement?.message || "");
              setBannerType(data.announcement?.type || "info");
              setBannerActive(data.announcement?.active || false);
          }

          // E. Fetch Dictionary
          const dictSnap = await getDoc(doc(db, "metadata", "dictionary"));
          if (dictSnap.exists()) {
              const dData = dictSnap.data();
              setDictionaryIngredients(dData.ingredients || []);
              setDictionaryCuisines(dData.cuisines || []);
          }

          // F. Calculate Analytics & Tags locally
          calculateAnalytics(usersList, recipesList, feedbackList);
          extractTags(recipesList);

      } catch (error) {
          console.error("Error fetching admin data:", error);
          showSnackbar("Error fetching dashboard data", "error");
      } finally {
          setIsLoading(false);
      }
  };

  // --- 3. LOCAL ANALYTICS HELPERS ---
  const extractTags = (recipesList) => {
      const tagMap = {};
      recipesList.forEach(recipe => {
          if (recipe.tags && Array.isArray(recipe.tags)) {
              recipe.tags.forEach(tag => {
                  const t = tag?.trim();
                  if (t) {
                      tagMap[t] = (tagMap[t] || 0) + 1;
                  }
              });
          }
      });
      const tagArray = Object.keys(tagMap)
          .map(name => ({ id: name, name, count: tagMap[name] }))
          .sort((a, b) => b.count - a.count);
      setTags(tagArray);
      return tagArray;
  };

  const calculateAnalytics = (uList, rList, fList) => {
      // 1. Tag Data for Pie Chart
      const tagArray = extractTags(rList);
      const pieData = tagArray
          .slice(0, 6)
          .map(t => ({ name: t.name, value: t.count }));

      // 2. Top Rated Recipes (sorted by star rating then rating count)
      const topRecipes = [...rList]
          .sort((a, b) => {
              const ratingDiff = (b.rating || 0) - (a.rating || 0);
              if (ratingDiff !== 0) return ratingDiff;
              return (b.rating_count || 0) - (a.rating_count || 0);
          })
          .slice(0, 5)
          .map(r => ({
              id: r.id,
              name: r.recipe_name || "Untitled",
              author: r.username || "Chef",
              rating: r.rating ? Number(r.rating).toFixed(1) : "—",
              rating_count: r.rating_count || 0
          }));

      // 3. Top Contributors
      const chefMap = {};
      rList.forEach(r => {
          const chef = r.username?.trim() || "Anonymous";
          chefMap[chef] = (chefMap[chef] || 0) + 1;
      });
      const topChefs = Object.keys(chefMap)
          .map(name => ({ id: name, username: name, count: chefMap[name] }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

      // 4. Most Favorited / Bookmarked Recipes
      const favMap = {};
      uList.forEach(u => {
          if (u.favoriteIds && Array.isArray(u.favoriteIds)) {
              u.favoriteIds.forEach(recipeId => {
                  favMap[recipeId] = (favMap[recipeId] || 0) + 1;
              });
          }
      });
      const totalFavorites = Object.values(favMap).reduce((sum, count) => sum + count, 0);

      const topFavorited = [...rList]
          .map(r => ({
              id: r.id,
              name: r.recipe_name || "Untitled",
              author: r.username || "Chef",
              favCount: favMap[r.id] || 0
          }))
          .sort((a, b) => b.favCount - a.favCount)
          .slice(0, 5);

      // 5. Cuisine Distribution
      const cuisineMap = {};
      rList.forEach(r => {
          const c = r.cuisine?.trim() || "General";
          cuisineMap[c] = (cuisineMap[c] || 0) + 1;
      });
      const cuisineData = Object.keys(cuisineMap)
          .map(name => ({ name, count: cuisineMap[name] }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6);

      // 6. Kitchen Profile: Difficulty, Times, Quick Meals, Photos
      const difficultyMap = { Easy: 0, Medium: 0, Hard: 0 };
      let totalPrepTime = 0;
      let totalCookTime = 0;
      let timeCount = 0;
      let quickMealsCount = 0;
      let withPhotoCount = 0;

      rList.forEach(r => {
          const diff = r.difficulty || "Medium";
          if (difficultyMap[diff] !== undefined) {
              difficultyMap[diff]++;
          } else {
              difficultyMap.Medium++;
          }

          const prep = Number(r.prep_time) || 0;
          const cook = Number(r.cook_time) || 0;
          if (prep > 0 || cook > 0) {
              totalPrepTime += prep;
              totalCookTime += cook;
              timeCount++;
          }
          if ((prep + cook) > 0 && (prep + cook) <= 30) {
              quickMealsCount++;
          }
          if ((r.images && r.images.length > 0) || r.image) {
              withPhotoCount++;
          }
      });

      const avgPrepTime = timeCount > 0 ? Math.round(totalPrepTime / timeCount) : 0;
      const avgCookTime = timeCount > 0 ? Math.round(totalCookTime / timeCount) : 0;
      const photoCoveragePct = rList.length > 0 ? Math.round((withPhotoCount / rList.length) * 100) : 0;

      // 7. Aggregate KPIs
      const totalReviews = rList.reduce((acc, r) => acc + (r.rating_count || 0), 0);
      const ratedRecipes = rList.filter(r => (r.rating_count || 0) > 0 && (r.rating || 0) > 0);
      const avgRating = ratedRecipes.length > 0 
          ? (ratedRecipes.reduce((acc, r) => acc + r.rating, 0) / ratedRecipes.length).toFixed(1) 
          : "—";

      // 8. Growth Timeline (Real Dates aggregated by month)
      const now = new Date();
      const monthBuckets = [];
      for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const endOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
          const label = d.toLocaleString('default', { month: 'short' });
          monthBuckets.push({ label, endOfMonth });
      }

      const chartData = monthBuckets.map(bucket => {
          const usersUpToDate = uList.filter(u => {
              const dt = parseDate(u.created_at);
              return !dt || dt <= bucket.endOfMonth;
          }).length;

          const recipesUpToDate = rList.filter(r => {
              const dt = parseDate(r.created_at);
              return !dt || dt <= bucket.endOfMonth;
          }).length;

          return {
              name: bucket.label,
              Users: usersUpToDate,
              Recipes: recipesUpToDate
          };
      });

      // Recipe additions in current month
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const newRecipesThisMonth = rList.filter(r => {
          const dt = parseDate(r.created_at);
          return dt && dt >= currentMonthStart;
      }).length;

      // Contributors count
      const activeContributorsCount = Object.keys(chefMap).length;

      setAnalytics({
          kpi: { 
              users: uList.length, 
              recipes: rList.length, 
              feedback: fList.length,
              reviews: totalReviews,
              avgRating: avgRating,
              totalFavorites: totalFavorites,
              quickMeals: quickMealsCount,
              newRecipesThisMonth: newRecipesThisMonth,
              activeContributors: activeContributorsCount,
              avgPrepTime: avgPrepTime,
              avgCookTime: avgCookTime,
              photoCoverage: photoCoveragePct,
              difficulties: difficultyMap
          },
          tag_data: pieData,
          cuisine_data: cuisineData,
          top_recipes: topRecipes,
          top_favorited: topFavorited,
          top_chefs: topChefs,
          chart_data: chartData
      });
  };

  // Recipe count per author mapping
  const recipeCountsByUser = useMemo(() => {
      const counts = {};
      recipes.forEach(r => {
          if (r.user_id) {
              counts[r.user_id] = (counts[r.user_id] || 0) + 1;
          }
          if (r.username) {
              counts[r.username] = (counts[r.username] || 0) + 1;
          }
      });
      return counts;
  }, [recipes]);

  // Distinct cuisines for filter dropdown
  const uniqueCuisines = useMemo(() => {
      const set = new Set();
      recipes.forEach(r => {
          if (r.cuisine && r.cuisine.trim()) {
              set.add(r.cuisine.trim());
          }
      });
      return Array.from(set).sort();
  }, [recipes]);

  // --- 4. ACTION HANDLERS ---

  // USERS
  const handleUserRoleChange = async (userId, newRole) => {
      try {
          const userRef = doc(db, "users", userId);
          await updateDoc(userRef, { role: newRole });
          setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
          showSnackbar(`User role updated to ${newRole}.`);
      } catch (err) { 
          console.error(err);
          showSnackbar("Failed to update role", "error"); 
      }
  };

  const handleUserDelete = async (userId, username) => {
      if (!window.confirm(`Delete user "${username || 'User'}" from database? This removes their profile document.`)) return;
      try {
          await deleteDoc(doc(db, "users", userId));
          setUsers(users.filter(u => u.id !== userId));
          showSnackbar(`Deleted profile for ${username || 'User'}.`);
      } catch (err) { 
          console.error(err);
          showSnackbar("Error deleting user profile", "error"); 
      }
  };

  // FEEDBACK
  const handleStatusChange = async (id, newStatus) => {
      try {
          await updateDoc(doc(db, "feedback", id), { status: newStatus });
          setFeedback(feedback.map(f => f.id === id ? { ...f, status: newStatus } : f));
          showSnackbar(`Feedback status updated to ${newStatus.replace('_', ' ')}.`);
      } catch (err) { 
          console.error(err);
          showSnackbar("Failed to update status", "error");
      }
  };

  const handleFeedbackDelete = async (id) => {
      if (!window.confirm("Delete this feedback ticket?")) return;
      try {
          await deleteDoc(doc(db, "feedback", id));
          setFeedback(feedback.filter(f => f.id !== id));
          showSnackbar("Feedback ticket deleted.");
      } catch (err) { 
          console.error(err);
          showSnackbar("Failed to delete feedback", "error");
      }
  };

  const saveEditFeedback = async () => {
      if (!editingFeedback) return;
      try {
          await updateDoc(doc(db, "feedback", editingFeedback.id), { 
              title: editTitle, 
              description: editDesc 
          });
          setFeedback(feedback.map(f => f.id === editingFeedback.id ? { ...f, title: editTitle, description: editDesc } : f));
          setEditingFeedback(null);
          showSnackbar("Feedback ticket updated successfully.");
      } catch (err) { 
          console.error(err);
          showSnackbar("Failed to update feedback", "error"); 
      }
  };

  // RECIPES
  const handleToggleFeature = async (recipe) => {
      const newVal = !recipe.is_featured;
      try {
          await updateDoc(doc(db, "recipes", recipe.id), { is_featured: newVal });
          setRecipes(recipes.map(r => r.id === recipe.id ? { ...r, is_featured: newVal } : r));
          showSnackbar(newVal ? `Marked "${recipe.recipe_name}" as Featured!` : `Removed "${recipe.recipe_name}" from Featured.`);
      } catch (err) { 
          console.error(err);
          showSnackbar("Failed to toggle featured status", "error"); 
      }
  };

  const handleRecipeDelete = async (id, title) => {
      if (!window.confirm(`Delete recipe "${title || 'Untitled'}"? This cannot be undone.`)) return;
      try {
          await deleteDoc(doc(db, "recipes", id));
          const updated = recipes.filter(r => r.id !== id);
          setRecipes(updated);
          calculateAnalytics(users, updated, feedback);
          extractTags(updated);
          showSnackbar(`Recipe "${title || 'Untitled'}" deleted.`);
      } catch (err) { 
          console.error(err);
          showSnackbar("Delete failed", "error"); 
      }
  };

  // TAGS: RENAME TAG
  const handleRenameSubmit = async () => {
      if (!tagToRename || !newTagName.trim()) return;
      const oldName = tagToRename.name;
      const trimmedNew = newTagName.trim();

      if (oldName === trimmedNew) {
          setRenameDialogOpen(false);
          return;
      }

      const recipesToUpdate = recipes.filter(r => r.tags && r.tags.includes(oldName));

      try {
          const promises = recipesToUpdate.map(async (recipe) => {
              const updatedTags = recipe.tags.map(t => t === oldName ? trimmedNew : t);
              // Deduplicate
              const uniqueTags = Array.from(new Set(updatedTags));
              const rRef = doc(db, "recipes", recipe.id);
              await updateDoc(rRef, { tags: uniqueTags });
              return { id: recipe.id, tags: uniqueTags };
          });

          await Promise.all(promises);

          const updatedRecipes = recipes.map(r => {
              if (r.tags && r.tags.includes(oldName)) {
                  const updatedTags = r.tags.map(t => t === oldName ? trimmedNew : t);
                  return { ...r, tags: Array.from(new Set(updatedTags)) };
              }
              return r;
          });

          setRecipes(updatedRecipes);
          extractTags(updatedRecipes);
          setRenameDialogOpen(false);
          setTagToRename(null);
          setNewTagName("");
          showSnackbar(`Renamed tag "${oldName}" to "${trimmedNew}" across ${recipesToUpdate.length} recipes.`);
      } catch (err) {
          console.error("Rename tag error:", err);
          showSnackbar("Failed to rename tag", "error");
      }
  };

  // TAGS: DELETE TAG
  const handleDeleteTag = async (tag) => {
      if (!window.confirm(`Are you sure you want to delete tag "${tag.name}"? It will be removed from all ${tag.count} recipes.`)) return;

      const recipesToUpdate = recipes.filter(r => r.tags && r.tags.includes(tag.name));

      try {
          const promises = recipesToUpdate.map(async (recipe) => {
              const updatedTags = recipe.tags.filter(t => t !== tag.name);
              const rRef = doc(db, "recipes", recipe.id);
              await updateDoc(rRef, { tags: updatedTags });
              return { id: recipe.id, tags: updatedTags };
          });

          await Promise.all(promises);

          const updatedRecipes = recipes.map(r => {
              if (r.tags && r.tags.includes(tag.name)) {
                  return { ...r, tags: r.tags.filter(t => t !== tag.name) };
              }
              return r;
          });

          setRecipes(updatedRecipes);
          extractTags(updatedRecipes);
          showSnackbar(`Deleted tag "${tag.name}" from ${recipesToUpdate.length} recipes.`);
      } catch (err) {
          console.error("Delete tag error:", err);
          showSnackbar("Failed to delete tag", "error");
      }
  };

  // TAGS: MERGE TAG
  const handleMergeSubmit = async () => {
      if (!targetTag || !tagToMerge) return;
      const sourceName = tagToMerge.name;
      const targetName = targetTag; 

      const recipesToUpdate = recipes.filter(r => r.tags && r.tags.includes(sourceName));
      
      try {
          const promises = recipesToUpdate.map(async (recipe) => {
              const newTags = recipe.tags.filter(t => t !== sourceName);
              if (!newTags.includes(targetName)) newTags.push(targetName);
              
              const rRef = doc(db, "recipes", recipe.id);
              await updateDoc(rRef, { tags: newTags });
              return { id: recipe.id, tags: newTags };
          });

          await Promise.all(promises);
          
          const updatedRecipes = recipes.map(r => {
              if (r.tags && r.tags.includes(sourceName)) {
                  const newTags = r.tags.filter(t => t !== sourceName);
                  if (!newTags.includes(targetName)) newTags.push(targetName);
                  return { ...r, tags: newTags };
              }
              return r;
          });
          
          setRecipes(updatedRecipes);
          extractTags(updatedRecipes); 
          setMergeDialogOpen(false);
          setTagToMerge(null);
          setTargetTag("");
          showSnackbar(`Merged "${sourceName}" into "${targetName}" across ${recipesToUpdate.length} recipes.`);
      } catch (err) {
          console.error(err);
          showSnackbar("Merge failed", "error");
      }
  };

  // SETTINGS
  const saveSettings = async () => {
      try {
          const settingsRef = doc(db, "settings", "global");
          await setDoc(settingsRef, {
              announcement: {
                  message: bannerMsg,
                  type: bannerType,
                  active: bannerActive
              }
          }, { merge: true });
          setBannerSaved(true);
          showSnackbar("Site settings saved successfully!");
          setTimeout(() => setBannerSaved(false), 3000);
      } catch (err) { 
          console.error(err);
          showSnackbar("Failed to save settings", "error"); 
      }
  };

  const handleExportRecipes = () => {
      const headers = [
          "Recipe ID", 
          "Recipe Name", 
          "Author", 
          "Cuisine", 
          "Difficulty", 
          "Servings", 
          "Prep Time (min)", 
          "Cook Time (min)", 
          "Rating", 
          "Rating Count", 
          "Tags", 
          "Created At"
      ];

      const escapeCsv = (val) => {
          if (val === null || val === undefined) return '""';
          const str = String(val).replace(/"/g, '""');
          return `"${str}"`;
      };

      const rows = recipes.map(r => {
          const dt = parseDate(r.created_at);
          return [
              escapeCsv(r.id),
              escapeCsv(r.recipe_name || "Untitled"),
              escapeCsv(r.username || "Unknown"),
              escapeCsv(r.cuisine || "General"),
              escapeCsv(r.difficulty || "Medium"),
              escapeCsv(r.servings || 1),
              escapeCsv(r.prep_time || 0),
              escapeCsv(r.cook_time || 0),
              escapeCsv(r.rating || 0),
              escapeCsv(r.rating_count || 0),
              escapeCsv((r.tags || []).join(", ")),
              escapeCsv(dt ? dt.toISOString() : "")
          ].join(",");
      });

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `astrup_recipes_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showSnackbar("Recipes CSV exported successfully!");
  };

  // --- DICTIONARY HANDLERS ---
  const handleAddIngredient = async () => {
    if (!newIngName.trim()) return;
    const clean = toTitleCase(newIngName.trim());
    if (dictionaryIngredients.includes(clean)) {
      showSnackbar(`"${clean}" is already in the dictionary.`, "warning");
      return;
    }
    const updated = [...dictionaryIngredients, clean].sort();
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { ingredients: updated });
      setDictionaryIngredients(updated);
      setNewIngName("");
      setAddIngOpen(false);
      showSnackbar(`Added "${clean}" to autocomplete dictionary.`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to add ingredient.", "error");
    }
  };

  const handleEditIngredient = async () => {
    if (!editedIngName.trim() || !ingToEdit) return;
    const clean = toTitleCase(editedIngName.trim());
    const updated = dictionaryIngredients.map(item => item === ingToEdit ? clean : item).sort();
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { ingredients: updated });
      setDictionaryIngredients(updated);
      setIngToEdit(null);
      setEditedIngName("");
      setEditIngOpen(false);
      showSnackbar(`Updated ingredient to "${clean}".`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update ingredient.", "error");
    }
  };

  const handleDeleteIngredient = async () => {
    if (!ingToDelete) return;
    const updated = dictionaryIngredients.filter(item => item !== ingToDelete);
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { ingredients: updated });
      setDictionaryIngredients(updated);
      setIngToDelete(null);
      setDeleteIngOpen(false);
      showSnackbar(`Removed "${ingToDelete}" from autocomplete dictionary.`, "info");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to remove ingredient.", "error");
    }
  };

  const handleResetIngredients = async () => {
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { ingredients: SEED_INGREDIENTS });
      setDictionaryIngredients(SEED_INGREDIENTS);
      setResetDialogOpen(false);
      showSnackbar("Reset ingredients to curated standard seed list.", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to reset ingredients.", "error");
    }
  };

  const handleAddCuisine = async () => {
    if (!newCuisineName.trim()) return;
    const clean = toTitleCase(newCuisineName.trim());
    if (dictionaryCuisines.includes(clean)) {
      showSnackbar(`"${clean}" is already in the cuisine list.`, "warning");
      return;
    }
    const updated = [...dictionaryCuisines, clean].sort();
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { cuisines: updated });
      setDictionaryCuisines(updated);
      setNewCuisineName("");
      setAddCuisineOpen(false);
      showSnackbar(`Added "${clean}" to cuisines list.`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to add cuisine.", "error");
    }
  };

  const handleEditCuisine = async () => {
    if (!editedCuisineName.trim() || !cuisineToEdit) return;
    const clean = toTitleCase(editedCuisineName.trim());
    const updated = dictionaryCuisines.map(item => item === cuisineToEdit ? clean : item).sort();
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { cuisines: updated });
      setDictionaryCuisines(updated);
      setCuisineToEdit(null);
      setEditedCuisineName("");
      setEditCuisineOpen(false);
      showSnackbar(`Updated cuisine to "${clean}".`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update cuisine.", "error");
    }
  };

  const handleDeleteCuisine = async () => {
    if (!cuisineToDelete) return;
    const updated = dictionaryCuisines.filter(item => item !== cuisineToDelete);
    try {
      await updateDoc(doc(db, "metadata", "dictionary"), { cuisines: updated });
      setDictionaryCuisines(updated);
      setCuisineToDelete(null);
      setDeleteCuisineOpen(false);
      showSnackbar(`Removed "${cuisineToDelete}" from cuisines.`, "info");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to remove cuisine.", "error");
    }
  };

  const handleMergeIngredientSubmit = async () => {
    if (!ingToMerge || !targetIng.trim()) return;
    const sourceName = ingToMerge.trim();
    const targetName = toTitleCase(targetIng.trim());

    if (sourceName.toLowerCase() === targetName.toLowerCase()) {
      showSnackbar("Source and target ingredients cannot be the same.", "warning");
      return;
    }

    setIsMergingIng(true);
    try {
      const sourceLower = sourceName.toLowerCase();

      // 1. Find all affected recipes
      const recipesToUpdate = recipes.filter(r => 
        r.ingredients && Array.isArray(r.ingredients) &&
        r.ingredients.some(ing => ing.ingredient_name && ing.ingredient_name.trim().toLowerCase() === sourceLower)
      );

      // 2. Update each affected recipe in Firestore, preserving amount, unit, and other fields
      const promises = recipesToUpdate.map(async (recipe) => {
        const newIngredients = recipe.ingredients.map(ing => {
          if (ing.ingredient_name && ing.ingredient_name.trim().toLowerCase() === sourceLower) {
            return {
              ...ing,
              ingredient_name: targetName
            };
          }
          return ing;
        });

        const rRef = doc(db, "recipes", recipe.id);
        await updateDoc(rRef, { ingredients: newIngredients });
        return { ...recipe, ingredients: newIngredients };
      });

      await Promise.all(promises);

      // 3. Update dictionary in Firestore
      const newDict = dictionaryIngredients.filter(i => i.trim().toLowerCase() !== sourceLower);
      if (!newDict.includes(targetName)) {
        newDict.push(targetName);
      }
      newDict.sort();

      const dictRef = doc(db, "metadata", "dictionary");
      await updateDoc(dictRef, { ingredients: newDict });

      // 4. Update local state
      const updatedRecipes = recipes.map(r => {
        const found = recipesToUpdate.find(u => u.id === r.id);
        if (found) {
          const newIngredients = r.ingredients.map(ing => {
            if (ing.ingredient_name && ing.ingredient_name.trim().toLowerCase() === sourceLower) {
              return { ...ing, ingredient_name: targetName };
            }
            return ing;
          });
          return { ...r, ingredients: newIngredients };
        }
        return r;
      });

      setRecipes(updatedRecipes);
      setDictionaryIngredients(newDict);
      setMergeIngOpen(false);
      setIngToMerge(null);
      setTargetIng("");
      showSnackbar(`Merged "${sourceName}" into "${targetName}" across ${recipesToUpdate.length} recipe(s) with all amounts and units preserved!`, "success");
    } catch (err) {
      console.error("Error merging ingredients:", err);
      showSnackbar("Failed to merge ingredients.", "error");
    } finally {
      setIsMergingIng(false);
    }
  };

  const handleMergeCuisineSubmit = async () => {
    if (!cuisineToMerge || !targetCuisine.trim()) return;
    const sourceName = cuisineToMerge.trim();
    const targetName = toTitleCase(targetCuisine.trim());

    if (sourceName.toLowerCase() === targetName.toLowerCase()) {
      showSnackbar("Source and target cuisines cannot be the same.", "warning");
      return;
    }

    setIsMergingCuisine(true);
    try {
      const sourceLower = sourceName.toLowerCase();

      // 1. Find all affected recipes
      const recipesToUpdate = recipes.filter(r => 
        r.cuisine && r.cuisine.trim().toLowerCase() === sourceLower
      );

      // 2. Update each affected recipe in Firestore
      const promises = recipesToUpdate.map(async (recipe) => {
        const rRef = doc(db, "recipes", recipe.id);
        await updateDoc(rRef, { cuisine: targetName });
        return { ...recipe, cuisine: targetName };
      });

      await Promise.all(promises);

      // 3. Update dictionary in Firestore
      const newDict = dictionaryCuisines.filter(c => c.trim().toLowerCase() !== sourceLower);
      if (!newDict.includes(targetName)) {
        newDict.push(targetName);
      }
      newDict.sort();

      const dictRef = doc(db, "metadata", "dictionary");
      await updateDoc(dictRef, { cuisines: newDict });

      // 4. Update local state
      const updatedRecipes = recipes.map(r => {
        if (r.cuisine && r.cuisine.trim().toLowerCase() === sourceLower) {
          return { ...r, cuisine: targetName };
        }
        return r;
      });

      setRecipes(updatedRecipes);
      setDictionaryCuisines(newDict);
      setMergeCuisineOpen(false);
      setCuisineToMerge(null);
      setTargetCuisine("");
      showSnackbar(`Merged cuisine "${sourceName}" into "${targetName}" across ${recipesToUpdate.length} recipe(s)!`, "success");
    } catch (err) {
      console.error("Error merging cuisines:", err);
      showSnackbar("Failed to merge cuisines.", "error");
    } finally {
      setIsMergingCuisine(false);
    }
  };

  // --- 5. RENDER HELPERS ---
  const getStatusColor = (status) => {
      switch(status) { 
          case 'new': return 'default'; 
          case 'planned': return 'warning'; 
          case 'in_progress': return 'info'; 
          case 'completed': return 'success'; 
          case 'declined': return 'error';
          default: return 'default'; 
      }
  };

  if (!isAdmin || isLoading) {
      return (
          <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
              <CircularProgress />
          </Box>
      );
  }

  // --- FILTERED DATA SETS ---

  // Users Filtering
  const filteredUsers = users.filter(u => {
      const matchSearch = (
          (u.username && u.username.toLowerCase().includes(userSearch.toLowerCase())) ||
          (u.email && u.email.toLowerCase().includes(userSearch.toLowerCase())) ||
          (u.first_name && u.first_name.toLowerCase().includes(userSearch.toLowerCase())) ||
          (u.last_name && u.last_name.toLowerCase().includes(userSearch.toLowerCase()))
      );
      const matchRole = userRoleFilter === "all" || (u.role || "user") === userRoleFilter;
      return matchSearch && matchRole;
  });
  const paginatedUsers = filteredUsers.slice((userPage - 1) * ITEMS_PER_PAGE, userPage * ITEMS_PER_PAGE);

  // Feedback Filtering
  const filteredFeedback = feedback.filter(f => {
      const matchSearch = (
          (f.title && f.title.toLowerCase().includes(feedbackSearch.toLowerCase())) ||
          (f.description && f.description.toLowerCase().includes(feedbackSearch.toLowerCase())) ||
          (f.user_email && f.user_email.toLowerCase().includes(feedbackSearch.toLowerCase()))
      );
      const matchStatus = feedbackStatusFilter === "all" || (f.status || "new") === feedbackStatusFilter;
      const matchType = feedbackTypeFilter === "all" || (f.type || "feature") === feedbackTypeFilter;
      return matchSearch && matchStatus && matchType;
  });
  const paginatedFeedback = filteredFeedback.slice((feedbackPage - 1) * ITEMS_PER_PAGE, feedbackPage * ITEMS_PER_PAGE);

  // Recipes Filtering
  const filteredRecipes = recipes.filter(r => {
      const matchSearch = (
          (r.recipe_name && r.recipe_name.toLowerCase().includes(recipeSearch.toLowerCase())) ||
          (r.username && r.username.toLowerCase().includes(recipeSearch.toLowerCase())) ||
          (r.description && r.description.toLowerCase().includes(recipeSearch.toLowerCase())) ||
          (r.cuisine && r.cuisine.toLowerCase().includes(recipeSearch.toLowerCase()))
      );
      const matchCuisine = recipeCuisineFilter === "all" || r.cuisine === recipeCuisineFilter;
      const matchFeatured = (
          recipeFeaturedFilter === "all" ||
          (recipeFeaturedFilter === "featured" && r.is_featured) ||
          (recipeFeaturedFilter === "standard" && !r.is_featured)
      );
      return matchSearch && matchCuisine && matchFeatured;
  });
  const paginatedRecipes = filteredRecipes.slice((recipePage - 1) * ITEMS_PER_PAGE, recipePage * ITEMS_PER_PAGE);

  // Tags Filtering
  const filteredTags = tags.filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase()));

  // Dictionary Filtering & Pagination
  const filteredIngredients = dictSearch.trim()
    ? dictionaryIngredients.filter(i => i.toLowerCase().includes(dictSearch.toLowerCase().trim()))
    : dictionaryIngredients;

  const totalDictPages = Math.ceil(filteredIngredients.length / DICT_ITEMS_PER_PAGE) || 1;
  const paginatedIngredients = filteredIngredients.slice((dictPage - 1) * DICT_ITEMS_PER_PAGE, dictPage * DICT_ITEMS_PER_PAGE);

  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', px: { xs: 2, sm: 3, md: 4, lg: 5 }, mb: 8, boxSizing: 'border-box' }}>
      <Typography variant="h4" gutterBottom sx={{ mt: 3, fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
        Admin Dashboard
      </Typography>
      
      <Paper elevation={3} sx={{ mb: 4, width: '100%' }}>
        <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)} 
            indicatorColor="primary" 
            textColor="primary" 
            variant="scrollable"
            scrollButtons="auto"
        >
            <Tab label="Overview & Analytics" />
            <Tab label={`Users (${users.length})`} />
            <Tab label={`Feedback (${feedback.length})`} />
            <Tab label={`Recipes (${recipes.length})`} />
            <Tab label={`Tags (${tags.length})`} />
            <Tab label={`Ingredients (${dictionaryIngredients.length})`} />
            <Tab label="Settings & Export" />
        </Tabs>
      </Paper>

      {/* ========================================================================= */}
      {/* --- TAB 0: OVERVIEW & ANALYTICS --- */}
      {/* ========================================================================= */}
      {tabValue === 0 && analytics && (
        <Box sx={{ width: '100%' }}>
            {/* Top Full-Width KPI Row (6 Metric Cards) */}
            <Grid container spacing={2.5} sx={{ mb: 3.5, width: '100%' }}>
                {/* 1. Total Recipes */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', height: '100%' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <RestaurantMenuIcon sx={{ fontSize: 28, color: '#2e7d32' }} />
                                {analytics.kpi?.newRecipesThisMonth > 0 && (
                                    <Chip label={`+${analytics.kpi?.newRecipesThisMonth} this mo`} size="small" color="success" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }} />
                                )}
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="#2e7d32" sx={{ mt: 1 }}>
                                {analytics.kpi?.recipes}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontWeight="medium">
                                Total Recipes
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 2. Family Members */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Card sx={{ bgcolor: '#e3f2fd', border: '1px solid #bbdefb', height: '100%' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <GroupIcon sx={{ fontSize: 28, color: '#1565c0' }} />
                                <Chip label={`${analytics.kpi?.activeContributors} chefs`} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="#1565c0" sx={{ mt: 1 }}>
                                {analytics.kpi?.users}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontWeight="medium">
                                Family Members
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 3. Cookbook Rating */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Card sx={{ bgcolor: '#fffde7', border: '1px solid #fff59d', height: '100%' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <StarIcon sx={{ fontSize: 28, color: '#fbc02d' }} />
                                <Chip label={`${analytics.kpi?.reviews} ratings`} size="small" sx={{ bgcolor: '#fff9c4', height: 20, fontSize: '0.65rem' }} />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="#f57f17" sx={{ mt: 1 }}>
                                {analytics.kpi?.avgRating} <Typography component="span" variant="body1" color="textSecondary">/ 5</Typography>
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontWeight="medium">
                                Avg Cookbook Rating
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 4. Family Favorites */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Card sx={{ bgcolor: '#fce4ec', border: '1px solid #f8bbd0', height: '100%' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <FavoriteIcon sx={{ fontSize: 28, color: '#c2185b' }} />
                                <Chip label="Bookmarks" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="#c2185b" sx={{ mt: 1 }}>
                                {analytics.kpi?.totalFavorites}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontWeight="medium">
                                Family Favorites Saved
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 5. Quick Meals */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Card sx={{ bgcolor: '#f3e5f5', border: '1px solid #e1bee7', height: '100%' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <AccessTimeIcon sx={{ fontSize: 28, color: '#7b1fa2' }} />
                                <Chip label="≤ 30 mins" size="small" sx={{ bgcolor: '#ede7f6', height: 20, fontSize: '0.65rem' }} />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="#7b1fa2" sx={{ mt: 1 }}>
                                {analytics.kpi?.quickMeals}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontWeight="medium">
                                Quick Weeknight Meals
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 6. Feedback & Ideas */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ffe0b2', height: '100%' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <FeedbackIcon sx={{ fontSize: 28, color: '#e65100' }} />
                                <Chip label="Roadmap" size="small" sx={{ bgcolor: '#ffe0b2', height: 20, fontSize: '0.65rem' }} />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="#e65100" sx={{ mt: 1 }}>
                                {analytics.kpi?.feedback}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontWeight="medium">
                                Feedback & Suggestions
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Charts & Leaderboards Row */}
            <Grid container spacing={3} sx={{ width: '100%' }}>
                {/* Left 8 Columns: Growth Timeline, Top Rated, Most Favorited, Kitchen Insights */}
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Stack spacing={3}>
                        {/* 1. Growth Chart */}
                        <Paper elevation={3} sx={{ p: 3, height: '420px', width: '100%', boxSizing: 'border-box' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TrendingUpIcon color="primary" /> Cookbook Growth Timeline
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Last 6 Months (Cumulative)
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Total published recipes vs registered family members over time.
                            </Typography>
                            <ResponsiveContainer width="100%" height="82%">
                                <LineChart data={analytics.chart_data}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis allowDecimals={false} />
                                    <ChartTooltip />
                                    <Legend verticalAlign="top" height={36} />
                                    <Line type="monotone" dataKey="Recipes" stroke="#388e3c" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="Users" stroke="#1976d2" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>

                        {/* 2. Side-by-Side Leaderboards: Top Rated vs Most Favorited */}
                        <Grid container spacing={3} sx={{ width: '100%' }}>
                            {/* Top Rated Recipes */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={3} sx={{ p: 3, height: '100%', minHeight: '360px', boxSizing: 'border-box' }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                                        <EmojiEventsIcon sx={{ color: '#FFD700' }} /> Top Rated Recipes
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Recipe</TableCell>
                                                <TableCell align="right">Rating</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {analytics.top_recipes.length > 0 ? (
                                                analytics.top_recipes.map((r, index) => (
                                                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/recipes/${r.id}`)}>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <Avatar sx={{ bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#e0e0e0', color: '#000', width: 24, height: 24, fontSize: 12, fontWeight: 'bold' }}>
                                                                    {index + 1}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ maxWidth: { xs: 150, sm: 180, md: 200 } }}>
                                                                        {r.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        by {r.author}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Chip 
                                                                icon={<StarIcon sx={{ color: '#FFB400 !important', fontSize: 16 }} />} 
                                                                label={`${r.rating} (${r.rating_count})`} 
                                                                size="small" 
                                                                variant="outlined" 
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={2} align="center">
                                                        <Typography color="textSecondary" sx={{ py: 2 }}>No recipes rated yet</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </Grid>

                            {/* Most Favorited / Bookmarked */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={3} sx={{ p: 3, height: '100%', minHeight: '360px', boxSizing: 'border-box' }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                                        <FavoriteIcon sx={{ color: '#e91e63' }} /> Family Favorites
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Recipe</TableCell>
                                                <TableCell align="right">Saved</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {analytics.top_favorited.length > 0 ? (
                                                analytics.top_favorited.map((r, index) => (
                                                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/recipes/${r.id}`)}>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <Avatar sx={{ bgcolor: '#fce4ec', color: '#c2185b', width: 24, height: 24, fontSize: 12, fontWeight: 'bold' }}>
                                                                    {index + 1}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ maxWidth: { xs: 150, sm: 180, md: 200 } }}>
                                                                        {r.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        by {r.author}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Chip 
                                                                icon={<FavoriteIcon sx={{ color: '#e91e63 !important', fontSize: 14 }} />} 
                                                                label={`${r.favCount} saves`} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="secondary"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={2} align="center">
                                                        <Typography color="textSecondary" sx={{ py: 2 }}>No favorited recipes yet</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* 3. Kitchen & Cooking Profile Card */}
                        <Paper elevation={3} sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon color="primary" /> Culinary Profile & Complexity
                            </Typography>
                            <Divider sx={{ mb: 2.5 }} />

                            <Grid container spacing={3} alignItems="center">
                                {/* Cooking Times */}
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, textAlign: 'center', border: '1px solid #eee' }}>
                                        <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block">
                                            AVERAGE TIME
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ mt: 0.5 }}>
                                            {analytics.kpi?.avgPrepTime + analytics.kpi?.avgCookTime} mins
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Prep: {analytics.kpi?.avgPrepTime}m • Cook: {analytics.kpi?.avgCookTime}m
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Photo Coverage */}
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, textAlign: 'center', border: '1px solid #eee' }}>
                                        <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block">
                                            PHOTO COVERAGE
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" color="#2e7d32" sx={{ mt: 0.5 }}>
                                            {analytics.kpi?.photoCoverage}%
                                        </Typography>
                                        <LinearProgress variant="determinate" value={analytics.kpi?.photoCoverage || 0} color="success" sx={{ height: 6, borderRadius: 3, mt: 1 }} />
                                    </Box>
                                </Grid>

                                {/* Difficulty Breakdown */}
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #eee' }}>
                                        <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" sx={{ mb: 1, textAlign: 'center' }}>
                                            DIFFICULTY BREAKDOWN
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" fontWeight="bold" color="#2e7d32">Easy:</Typography>
                                                <Typography variant="caption">{analytics.kpi?.difficulties?.Easy || 0} recipes</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" fontWeight="bold" color="#f57c00">Medium:</Typography>
                                                <Typography variant="caption">{analytics.kpi?.difficulties?.Medium || 0} recipes</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" fontWeight="bold" color="#c62828">Hard:</Typography>
                                                <Typography variant="caption">{analytics.kpi?.difficulties?.Hard || 0} recipes</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Stack>
                </Grid>

                {/* Right 4 Columns: Cuisine Breakdown, Tag Distribution, Top Contributors */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Stack spacing={3}>
                        {/* 1. Cuisine Breakdown */}
                        <Paper elevation={3} sx={{ p: 3, height: '360px', width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PublicIcon color="primary" sx={{ fontSize: 20 }} /> Recipes by Cuisine
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                                Most popular culinary styles in the cookbook.
                            </Typography>
                            {analytics.cuisine_data && analytics.cuisine_data.length > 0 ? (
                                <ResponsiveContainer width="100%" height="80%">
                                    <BarChart data={analytics.cuisine_data} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" allowDecimals={false} />
                                        <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                                        <ChartTooltip />
                                        <Bar dataKey="count" fill="#477491" radius={[0, 4, 4, 0]}>
                                            {analytics.cuisine_data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CUISINE_COLORS[index % CUISINE_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="70%">
                                    <Typography color="textSecondary">No cuisine data available</Typography>
                                </Box>
                            )}
                        </Paper>

                        {/* 2. Tag Distribution Chart */}
                        <Paper elevation={3} sx={{ p: 3, height: '360px', width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="subtitle1" fontWeight="bold" align="center" gutterBottom>
                                <LocalOfferIcon color="primary" sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                                Top Recipe Tags
                            </Typography>
                            {analytics.tag_data && analytics.tag_data.length > 0 ? (
                                <ResponsiveContainer width="100%" height="85%">
                                    <PieChart>
                                        <Pie 
                                            data={analytics.tag_data} 
                                            cx="50%" cy="45%" 
                                            innerRadius={45} 
                                            outerRadius="75%" 
                                            paddingAngle={5} 
                                            dataKey="value"
                                        >
                                            {analytics.tag_data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <ChartTooltip />
                                        <Legend verticalAlign="bottom" height={36} iconSize={10} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                                    <Typography color="textSecondary">No tag data available</Typography>
                                </Box>
                            )}
                        </Paper>

                        {/* 3. Top Contributors */}
                        <Paper elevation={3} sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                                <ChefHatIcon sx={{ color: '#FF7043' }} /> Top Contributors
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Chef</TableCell>
                                        <TableCell align="right">Recipes</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analytics.top_chefs.length > 0 ? (
                                        analytics.top_chefs.map((c, index) => (
                                            <TableRow key={c.id}>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Avatar sx={{ bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#FF7043', color: index < 3 ? '#000' : '#fff', width: 24, height: 24, fontSize: 12, fontWeight: 'bold' }}>
                                                            {index + 1}
                                                        </Avatar>
                                                        <Typography variant="subtitle2" fontWeight="bold">
                                                            {c.username}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip label={`${c.count} ${c.count === 1 ? 'recipe' : 'recipes'}`} color="primary" size="small" variant="filled" />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography color="textSecondary" sx={{ py: 2 }}>No contributors yet</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* --- TAB 1: USERS --- */}
      {/* ========================================================================= */}
      {tabValue === 1 && (
        <Paper elevation={3} sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
            {/* Filter Bar */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                    <TextField 
                        label="Search Users" 
                        variant="outlined" 
                        size="small" 
                        fullWidth 
                        value={userSearch} 
                        onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }} 
                        placeholder="Search by username, full name, or email..." 
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} 
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Filter by Role</InputLabel>
                        <Select 
                            value={userRoleFilter} 
                            label="Filter by Role"
                            onChange={(e) => { setUserRoleFilter(e.target.value); setUserPage(1); }}
                        >
                            <MenuItem value="all">All Roles</MenuItem>
                            <MenuItem value="admin">Admins Only</MenuItem>
                            <MenuItem value="user">Regular Users</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell align="center">Recipes Authored</TableCell>
                            <TableCell>Joined</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedUsers.length > 0 ? (
                            paginatedUsers.map(user => {
                                const authorCount = recipeCountsByUser[user.id] || recipeCountsByUser[user.username] || 0;
                                const isSelf = user.id === auth.currentUser?.uid;
                                const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");

                                return (
                                    <TableRow key={user.id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar 
                                                    src={user.photoUrl || user.photo_url || ""} 
                                                    sx={{ 
                                                        bgcolor: user.role === 'admin' ? '#FF7043' : '#477491',
                                                        width: 42, 
                                                        height: 42,
                                                        border: '2px solid #e0e0e0'
                                                    }}
                                                >
                                                    {getInitials(user.username || fullName || user.email)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {(user.username && !user.username.includes('@')) 
                                                          ? user.username 
                                                          : (fullName || (user.username ? user.username.split('@')[0] : "Unnamed Chef"))}
                                                        {isSelf && (
                                                            <Chip label="You" size="small" color="secondary" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                                        )}
                                                    </Typography>
                                                    {fullName && (
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            {fullName}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="textSecondary">
                                                        {user.email || "No email"}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Select 
                                                value={user.role || 'user'} 
                                                onChange={(e) => handleUserRoleChange(user.id, e.target.value)} 
                                                size="small" 
                                                disabled={isSelf} 
                                                sx={{ fontSize: '0.875rem', minWidth: 105 }}
                                            >
                                                <MenuItem value="user">User</MenuItem>
                                                <MenuItem value="admin">Admin</MenuItem>
                                            </Select>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip 
                                                label={`${authorCount} ${authorCount === 1 ? 'recipe' : 'recipes'}`} 
                                                size="small" 
                                                color={authorCount > 0 ? "primary" : "default"} 
                                                variant={authorCount > 0 ? "filled" : "outlined"}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDateTime(user.created_at)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={isSelf ? "Cannot delete yourself" : "Delete user profile"}>
                                                <span>
                                                    <IconButton 
                                                        color="error" 
                                                        onClick={() => handleUserDelete(user.id, user.username)} 
                                                        disabled={isSelf}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography color="textSecondary" sx={{ py: 3 }}>No users found matching your filters.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            {filteredUsers.length > ITEMS_PER_PAGE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination 
                        count={Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)} 
                        page={userPage} 
                        onChange={(e, v) => setUserPage(v)} 
                        color="primary" 
                        showFirstButton 
                        showLastButton 
                    />
                </Box>
            )}
        </Paper>
      )}

      {/* ========================================================================= */}
      {/* --- TAB 2: FEEDBACK & ROADMAP MODERATION --- */}
      {/* ========================================================================= */}
      {tabValue === 2 && (
        <Paper elevation={3} sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
            {/* Filter Bar */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                    <TextField 
                        label="Search Feedback" 
                        variant="outlined" 
                        size="small" 
                        fullWidth 
                        value={feedbackSearch} 
                        onChange={(e) => { setFeedbackSearch(e.target.value); setFeedbackPage(1); }} 
                        placeholder="Search title, description, or submitter..." 
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} 
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select 
                            value={feedbackStatusFilter} 
                            label="Status"
                            onChange={(e) => { setFeedbackStatusFilter(e.target.value); setFeedbackPage(1); }}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="new">🆕 New</MenuItem>
                            <MenuItem value="planned">📅 Planned</MenuItem>
                            <MenuItem value="in_progress">🚧 In Progress</MenuItem>
                            <MenuItem value="completed">✅ Completed</MenuItem>
                            <MenuItem value="declined">❌ Declined</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select 
                            value={feedbackTypeFilter} 
                            label="Type"
                            onChange={(e) => { setFeedbackTypeFilter(e.target.value); setFeedbackPage(1); }}
                        >
                            <MenuItem value="all">All Types</MenuItem>
                            <MenuItem value="feature">💡 Feature</MenuItem>
                            <MenuItem value="bug">🐛 Bug Fix</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Upvotes</TableCell>
                            <TableCell>Title / Description</TableCell>
                            <TableCell>Submitted By</TableCell>
                            <TableCell>Status (Public Roadmap)</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedFeedback.length > 0 ? (
                            paginatedFeedback.map(item => {
                                const upvoteCount = item.upvoted_by?.length || 0;

                                return (
                                    <TableRow key={item.id} hover>
                                        <TableCell>
                                            <Chip 
                                                icon={item.type === 'bug' ? <BugReportIcon /> : <LightbulbIcon />} 
                                                label={item.type === 'bug' ? 'Bug' : 'Feature'} 
                                                color={item.type === 'bug' ? 'error' : 'primary'} 
                                                variant="outlined" 
                                                size="small" 
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={`${upvoteCount} family member upvotes`}>
                                                <Chip 
                                                    icon={<ThumbUpIcon sx={{ fontSize: 13 }} />} 
                                                    label={upvoteCount} 
                                                    size="small" 
                                                    color={upvoteCount > 0 ? "primary" : "default"} 
                                                    variant={upvoteCount > 0 ? "filled" : "outlined"} 
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {item.title}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                color="textSecondary" 
                                                sx={{ 
                                                    display: '-webkit-box', 
                                                    WebkitLineClamp: 2, 
                                                    WebkitBoxOrient: 'vertical', 
                                                    overflow: 'hidden' 
                                                }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {item.user_email || 'Anonymous'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                {formatDateTime(item.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Select 
                                                value={item.status || 'new'} 
                                                onChange={(e) => handleStatusChange(item.id, e.target.value)} 
                                                size="small" 
                                                sx={{ minWidth: 140 }} 
                                                renderValue={(selected) => (
                                                    <Chip 
                                                        label={selected.replace('_', ' ').toUpperCase()} 
                                                        color={getStatusColor(selected)} 
                                                        size="small" 
                                                    />
                                                )}
                                            >
                                                <MenuItem value="new">🆕 New</MenuItem>
                                                <MenuItem value="planned">📅 Planned</MenuItem>
                                                <MenuItem value="in_progress">🚧 In Progress</MenuItem>
                                                <MenuItem value="completed">✅ Completed</MenuItem>
                                                <MenuItem value="declined">❌ Declined</MenuItem>
                                            </Select>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit Title/Description">
                                                <IconButton 
                                                    color="primary" 
                                                    onClick={() => { 
                                                        setEditingFeedback(item); 
                                                        setEditTitle(item.title); 
                                                        setEditDesc(item.description); 
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Feedback">
                                                <IconButton color="error" onClick={() => handleFeedbackDelete(item.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography color="textSecondary" sx={{ py: 3 }}>No feedback items match your filters.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            {filteredFeedback.length > ITEMS_PER_PAGE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination 
                        count={Math.ceil(filteredFeedback.length / ITEMS_PER_PAGE)} 
                        page={feedbackPage} 
                        onChange={(e, v) => setFeedbackPage(v)} 
                        color="primary" 
                        showFirstButton 
                        showLastButton 
                    />
                </Box>
            )}
        </Paper>
      )}

      {/* ========================================================================= */}
      {/* --- TAB 3: RECIPES --- */}
      {/* ========================================================================= */}
      {tabValue === 3 && (
        <Paper elevation={3} sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
            {/* Filter Bar */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                    <TextField 
                        label="Search Recipes" 
                        variant="outlined" 
                        size="small" 
                        fullWidth 
                        value={recipeSearch} 
                        onChange={(e) => { setRecipeSearch(e.target.value); setRecipePage(1); }} 
                        placeholder="Search by title, author, or cuisine..." 
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} 
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Cuisine</InputLabel>
                        <Select 
                            value={recipeCuisineFilter} 
                            label="Cuisine"
                            onChange={(e) => { setRecipeCuisineFilter(e.target.value); setRecipePage(1); }}
                        >
                            <MenuItem value="all">All Cuisines</MenuItem>
                            {uniqueCuisines.map(c => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Featured</InputLabel>
                        <Select 
                            value={recipeFeaturedFilter} 
                            label="Featured"
                            onChange={(e) => { setRecipeFeaturedFilter(e.target.value); setRecipePage(1); }}
                        >
                            <MenuItem value="all">All Recipes</MenuItem>
                            <MenuItem value="featured">⭐ Featured Only</MenuItem>
                            <MenuItem value="standard">Standard (Non-featured)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Featured</TableCell> 
                            <TableCell>Image</TableCell>
                            <TableCell>Recipe Name</TableCell>
                            <TableCell>Author</TableCell>
                            <TableCell>Rating</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRecipes.length > 0 ? (
                            paginatedRecipes.map(recipe => {
                                const thumbnail = (recipe.images && recipe.images.length > 0) 
                                    ? recipe.images[0] 
                                    : (recipe.image || "/no-image.png");

                                return (
                                    <TableRow key={recipe.id} hover>
                                        <TableCell align="center">
                                            <Tooltip title={recipe.is_featured ? "Featured on Home (Click to unfeature)" : "Click to mark as Featured"}>
                                                <IconButton 
                                                    onClick={() => handleToggleFeature(recipe)} 
                                                    color={recipe.is_featured ? "warning" : "default"}
                                                    size="small"
                                                >
                                                    {recipe.is_featured ? <StarIcon /> : <StarBorderIcon />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Avatar 
                                                variant="rounded" 
                                                src={thumbnail} 
                                                sx={{ width: 50, height: 50, border: '1px solid #e0e0e0' }}
                                                imgProps={{
                                                    onError: (e) => { e.target.src = "/no-image.png"; }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {recipe.recipe_name || "Untitled Recipe"}
                                            </Typography>
                                            {recipe.cuisine && (
                                                <Chip 
                                                    label={recipe.cuisine} 
                                                    size="small" 
                                                    variant="outlined" 
                                                    sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} 
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{recipe.username || "Unknown"}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <StarIcon sx={{ color: '#FFB400', fontSize: 18 }} />
                                                <Typography variant="body2" fontWeight="bold">
                                                    {recipe.rating ? Number(recipe.rating).toFixed(1) : "—"}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    ({recipe.rating_count || 0})
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatDateTime(recipe.created_at)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View Recipe">
                                                <IconButton color="primary" onClick={() => navigate(`/recipes/${recipe.id}`)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Recipe">
                                                <IconButton color="info" onClick={() => navigate(`/edit-recipe/${recipe.id}`)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Recipe">
                                                <IconButton color="error" onClick={() => handleRecipeDelete(recipe.id, recipe.recipe_name)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography color="textSecondary" sx={{ py: 3 }}>No recipes found matching your filters.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            {filteredRecipes.length > ITEMS_PER_PAGE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination 
                        count={Math.ceil(filteredRecipes.length / ITEMS_PER_PAGE)} 
                        page={recipePage} 
                        onChange={(e, v) => setRecipePage(v)} 
                        color="primary" 
                        showFirstButton 
                        showLastButton 
                    />
                </Box>
            )}
        </Paper>
      )}

      {/* ========================================================================= */}
      {/* --- TAB 4: TAG MANAGEMENT --- */}
      {/* ========================================================================= */}
      {tabValue === 4 && (
        <Paper elevation={3} sx={{ p: 3, maxWidth: '900px', mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Cookbook Tags</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Rename typos, merge duplicate tags, or clean up unused tags across all recipes.
                    </Typography>
                </Box>
                <Chip label={`${tags.length} Total Tags`} color="primary" variant="outlined" />
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField 
                    label="Search Tags" 
                    variant="outlined" 
                    size="small" 
                    fullWidth 
                    value={tagSearch} 
                    onChange={(e) => setTagSearch(e.target.value)} 
                    placeholder="Search by tag name..."
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
            </Box>

            <Box sx={{ overflowX: 'auto', maxHeight: '550px' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Tag</TableCell>
                            <TableCell align="center">Usage Count</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTags.length > 0 ? (
                            filteredTags.map((tag) => (
                                <TableRow key={tag.name} hover>
                                    <TableCell>
                                        <Chip label={tag.name} size="medium" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold">
                                            {tag.count} {tag.count === 1 ? 'recipe' : 'recipes'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {/* Rename Tag */}
                                        <Tooltip title="Rename Tag across all recipes">
                                            <IconButton 
                                                color="info" 
                                                onClick={() => {
                                                    setTagToRename(tag);
                                                    setNewTagName(tag.name);
                                                    setRenameDialogOpen(true);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        {/* Merge Tag */}
                                        <Tooltip title="Merge into another tag">
                                            <IconButton 
                                                color="primary" 
                                                onClick={() => { 
                                                    setTagToMerge(tag); 
                                                    setTargetTag("");
                                                    setMergeDialogOpen(true); 
                                                }}
                                            >
                                                <MergeTypeIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        {/* Delete Tag */}
                                        <Tooltip title="Delete tag from all recipes">
                                            <IconButton 
                                                color="error" 
                                                onClick={() => handleDeleteTag(tag)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <Typography color="textSecondary" sx={{ py: 3 }}>No tags found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
      )}

      {/* ========================================================================= */}
      {/* --- TAB 5: INGREDIENTS & CUISINES DICTIONARY --- */}
      {/* ========================================================================= */}
      {tabValue === 5 && (
        <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, width: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                        <RestaurantMenuIcon color="primary" /> Ingredients & Cuisines Autocomplete
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Manage the central master dictionary that powers real-time autocomplete across recipe creation and editing.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button 
                        variant="outlined" 
                        color="warning" 
                        size="small" 
                        onClick={() => setResetDialogOpen(true)}
                    >
                        Reset to Curated Ingredients
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        startIcon={<AddIcon />} 
                        onClick={() => setAddIngOpen(true)}
                    >
                        Add Ingredient
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        size="small" 
                        startIcon={<AddIcon />} 
                        onClick={() => setAddCuisineOpen(true)}
                    >
                        Add Cuisine
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* INGREDIENTS COLUMN */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" fontWeight="bold">
                                Master Ingredients ({dictionaryIngredients.length})
                            </Typography>
                            <TextField 
                                size="small" 
                                placeholder="Search ingredients..." 
                                value={dictSearch} 
                                onChange={(e) => { setDictSearch(e.target.value); setDictPage(1); }} 
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                                sx={{ width: 220 }}
                            />
                        </Box>

                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold', width: 60 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Ingredient Name</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', width: 120 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedIngredients.map((item, idx) => (
                                    <TableRow key={item} hover>
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {(dictPage - 1) * DICT_ITEMS_PER_PAGE + idx + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {item}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Merge ingredient into another">
                                                <IconButton 
                                                    size="small" 
                                                    color="secondary" 
                                                    onClick={() => { setIngToMerge(item); setTargetIng(""); setMergeIngOpen(true); }}
                                                >
                                                    <MergeTypeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Rename ingredient">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary" 
                                                    onClick={() => { setIngToEdit(item); setEditedIngName(item); setEditIngOpen(true); }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete from autocomplete dictionary">
                                                <IconButton 
                                                    size="small" 
                                                    color="error" 
                                                    onClick={() => { setIngToDelete(item); setDeleteIngOpen(true); }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {paginatedIngredients.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                            No ingredients found matching "{dictSearch}".
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {totalDictPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                                <Pagination 
                                    count={totalDictPages} 
                                    page={dictPage} 
                                    onChange={(e, p) => setDictPage(p)} 
                                    color="primary" 
                                    size="small"
                                />
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* CUISINES COLUMN */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">
                                Standard Cuisines ({dictionaryCuisines.length})
                            </Typography>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cuisine</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', width: 100 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dictionaryCuisines.map((item) => (
                                    <TableRow key={item} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{item}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Merge cuisine into another">
                                                <IconButton 
                                                    size="small" 
                                                    color="secondary" 
                                                    onClick={() => { setCuisineToMerge(item); setTargetCuisine(""); setMergeCuisineOpen(true); }}
                                                >
                                                    <MergeTypeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Rename cuisine">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary" 
                                                    onClick={() => { setCuisineToEdit(item); setEditedCuisineName(item); setEditCuisineOpen(true); }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete cuisine">
                                                <IconButton 
                                                    size="small" 
                                                    color="error" 
                                                    onClick={() => { setCuisineToDelete(item); setDeleteCuisineOpen(true); }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Grid>
        </Paper>
      )}

      {/* ========================================================================= */}
      {/* --- TAB 6: SYSTEM SETTINGS & EXPORT --- */}
      {/* ========================================================================= */}
      {tabValue === 6 && (
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
            <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, mb: 4, width: '100%', boxSizing: 'border-box' }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                    <CampaignIcon color="primary" /> Global Announcement Banner
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Display a persistent banner at the top of the Astrup Family Cookbook for all users (e.g. holiday greetings, new features, or dinner schedule updates).
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {/* PREVIEW */}
                <Box sx={{ mb: 4, p: 2, border: '1px dashed #bbb', borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" mb={1}>
                        LIVE BANNER PREVIEW:
                    </Typography>
                    {bannerActive ? (
                        <Alert severity={bannerType} variant="filled" sx={{ textAlign: 'center', justifyContent: 'center' }}>
                            {bannerMsg || "(No message entered)"}
                        </Alert>
                    ) : (
                        <Typography color="textDisabled" fontStyle="italic" align="center" sx={{ py: 1 }}>
                            Banner is currently turned OFF.
                        </Typography>
                    )}
                </Box>

                {/* FORM */}
                <Stack spacing={3}>
                    <TextField 
                        label="Announcement Message" 
                        fullWidth 
                        multiline 
                        rows={2} 
                        value={bannerMsg} 
                        onChange={(e) => setBannerMsg(e.target.value)} 
                        placeholder="e.g. Welcome to the new Astrup Family Cookbook! Try the new search filters."
                        helperText="Keep it short and friendly for mobile screens."
                    />
                    
                    <FormControl fullWidth>
                        <InputLabel>Banner Color / Severity</InputLabel>
                        <Select 
                            value={bannerType} 
                            label="Banner Color / Severity" 
                            onChange={(e) => setBannerType(e.target.value)}
                        >
                            <MenuItem value="info">🔵 Blue (Info / Notice)</MenuItem>
                            <MenuItem value="success">🟢 Green (Celebration / Success)</MenuItem>
                            <MenuItem value="warning">🟠 Orange (Reminder / Warning)</MenuItem>
                            <MenuItem value="error">🔴 Red (Urgent / Alert)</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControlLabel 
                        control={
                            <Switch 
                                checked={bannerActive} 
                                onChange={(e) => setBannerActive(e.target.checked)} 
                                color="primary" 
                            />
                        } 
                        label={
                            <Typography fontWeight="bold">
                                {bannerActive ? "Banner is ACTIVE (Visible to all users)" : "Banner is INACTIVE (Hidden)"}
                            </Typography>
                        } 
                    />

                    <Button 
                        variant="contained" 
                        size="large" 
                        onClick={saveSettings} 
                        disabled={bannerSaved}
                        sx={{ py: 1.5, fontWeight: 'bold' }}
                    >
                        {bannerSaved ? "Settings Saved!" : "Save Banner Settings"}
                    </Button>
                </Stack>

                <Divider sx={{ my: 4 }} />

                {/* DATA EXPORT */}
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                    <StorageIcon color="secondary" /> Data Management & Backup
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Export a full CSV spreadsheet of all {recipes.length} recipes in the cookbook, including ratings, prep times, ingredients metadata, and tags for safe keeping or printing.
                </Typography>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    size="large"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportRecipes}
                >
                    Download Recipes CSV ({recipes.length} Recipes)
                </Button>
            </Paper>
        </Container>
      )}

      {/* ========================================================================= */}
      {/* --- DIALOGS --- */}
      {/* ========================================================================= */}

      {/* 1. Rename Tag Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Rename Tag</DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Rename <strong>"{tagToRename?.name}"</strong> across all {tagToRename?.count} recipes that currently use it.
              </DialogContentText>
              <TextField 
                  label="New Tag Name" 
                  fullWidth 
                  autoFocus
                  margin="dense"
                  value={newTagName} 
                  onChange={(e) => setNewTagName(e.target.value)} 
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(); }}
              />
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
              <Button 
                  onClick={handleRenameSubmit} 
                  variant="contained" 
                  color="primary" 
                  disabled={!newTagName.trim()}
              >
                  Save Rename
              </Button>
          </DialogActions>
      </Dialog>

      {/* 2. Merge Tag Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Merge Tag</DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Merge <strong>"{tagToMerge?.name}"</strong> into another existing tag. 
                  All recipes currently labeled "{tagToMerge?.name}" will have it replaced by the target tag.
              </DialogContentText>
              <FormControl fullWidth margin="dense">
                  <InputLabel>Merge Into Tag...</InputLabel>
                  <Select 
                      value={targetTag} 
                      label="Merge Into Tag..." 
                      onChange={(e) => setTargetTag(e.target.value)}
                  >
                      {tags
                        .filter(t => t.name !== tagToMerge?.name)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((t) => (
                          <MenuItem key={t.name} value={t.name}>{t.name} ({t.count} recipes)</MenuItem>
                      ))}
                  </Select>
              </FormControl>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
              <Button 
                  onClick={handleMergeSubmit} 
                  variant="contained" 
                  color="primary" 
                  disabled={!targetTag}
              >
                  Confirm Merge
              </Button>
          </DialogActions>
      </Dialog>

      {/* 3. Edit Feedback Dialog */}
      <Dialog open={!!editingFeedback} onClose={() => setEditingFeedback(null)} fullWidth maxWidth="sm">
          <DialogTitle>Edit Feedback Ticket</DialogTitle>
          <DialogContent>
              <TextField 
                  label="Title" 
                  fullWidth 
                  margin="normal" 
                  value={editTitle} 
                  onChange={(e) => setEditTitle(e.target.value)} 
              />
              <TextField 
                  label="Description" 
                  fullWidth 
                  margin="normal" 
                  multiline 
                  rows={4} 
                  value={editDesc} 
                  onChange={(e) => setEditDesc(e.target.value)} 
              />
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setEditingFeedback(null)}>Cancel</Button>
              <Button onClick={saveEditFeedback} variant="contained" color="primary">Save Changes</Button>
          </DialogActions>
      </Dialog>

      {/* 4. Add Ingredient Dialog */}
      <Dialog open={addIngOpen} onClose={() => setAddIngOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Add Master Ingredient</DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Add a clean, standardized ingredient to the autocomplete dictionary.
              </DialogContentText>
              <TextField 
                  label="Ingredient Name" 
                  fullWidth 
                  autoFocus 
                  margin="dense"
                  placeholder="e.g. Dijon Mustard"
                  value={newIngName}
                  onChange={(e) => setNewIngName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddIngredient(); }}
              />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setAddIngOpen(false)}>Cancel</Button>
              <Button onClick={handleAddIngredient} variant="contained" color="primary" disabled={!newIngName.trim()}>
                  Add Ingredient
              </Button>
          </DialogActions>
      </Dialog>

      {/* 5. Edit Ingredient Dialog */}
      <Dialog open={editIngOpen} onClose={() => setEditIngOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Ingredient</DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Rename <strong>"{ingToEdit}"</strong> in the master autocomplete list.
              </DialogContentText>
              <TextField 
                  label="New Name" 
                  fullWidth 
                  autoFocus 
                  margin="dense"
                  value={editedIngName}
                  onChange={(e) => setEditedIngName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEditIngredient(); }}
              />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setEditIngOpen(false)}>Cancel</Button>
              <Button onClick={handleEditIngredient} variant="contained" color="primary" disabled={!editedIngName.trim()}>
                  Save Rename
              </Button>
          </DialogActions>
      </Dialog>

      {/* 6. Delete Ingredient Dialog */}
      <Dialog open={deleteIngOpen} onClose={() => setDeleteIngOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>Delete Ingredient</DialogTitle>
          <DialogContent>
              <DialogContentText>
                  Are you sure you want to remove <strong>"{ingToDelete}"</strong> from the master autocomplete dictionary?
              </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setDeleteIngOpen(false)}>Cancel</Button>
              <Button onClick={handleDeleteIngredient} variant="contained" color="error">
                  Delete
              </Button>
          </DialogActions>
      </Dialog>

      {/* 7. Add Cuisine Dialog */}
      <Dialog open={addCuisineOpen} onClose={() => setAddCuisineOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Add Standard Cuisine</DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Add a new cuisine style to the cookbook's standard options.
              </DialogContentText>
              <TextField 
                  label="Cuisine Name" 
                  fullWidth 
                  autoFocus 
                  margin="dense"
                  placeholder="e.g. Korean"
                  value={newCuisineName}
                  onChange={(e) => setNewCuisineName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddCuisine(); }}
              />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setAddCuisineOpen(false)}>Cancel</Button>
              <Button onClick={handleAddCuisine} variant="contained" color="secondary" disabled={!newCuisineName.trim()}>
                  Add Cuisine
              </Button>
          </DialogActions>
      </Dialog>

      {/* 8. Edit Cuisine Dialog */}
      <Dialog open={editCuisineOpen} onClose={() => setEditCuisineOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Cuisine</DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Rename <strong>"{cuisineToEdit}"</strong> in the standard cuisine options.
              </DialogContentText>
              <TextField 
                  label="New Name" 
                  fullWidth 
                  autoFocus 
                  margin="dense"
                  value={editedCuisineName}
                  onChange={(e) => setEditedCuisineName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEditCuisine(); }}
              />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setEditCuisineOpen(false)}>Cancel</Button>
              <Button onClick={handleEditCuisine} variant="contained" color="secondary" disabled={!editedCuisineName.trim()}>
                  Save Rename
              </Button>
          </DialogActions>
      </Dialog>

      {/* 9. Delete Cuisine Dialog */}
      <Dialog open={deleteCuisineOpen} onClose={() => setDeleteCuisineOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>Delete Cuisine</DialogTitle>
          <DialogContent>
              <DialogContentText>
                  Are you sure you want to remove <strong>"{cuisineToDelete}"</strong> from the standard cuisine list?
              </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setDeleteCuisineOpen(false)}>Cancel</Button>
              <Button onClick={handleDeleteCuisine} variant="contained" color="error">
                  Delete
              </Button>
          </DialogActions>
      </Dialog>

      {/* 10. Reset Ingredients Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main' }}>Reset to Curated Ingredients</DialogTitle>
          <DialogContent>
              <DialogContentText>
                  This will reset the autocomplete dictionary to the default 140+ curated culinary ingredients, removing any unverified or custom entries. Existing recipes will remain untouched. Are you sure?
              </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleResetIngredients} variant="contained" color="warning">
                  Confirm Reset
              </Button>
          </DialogActions>
      </Dialog>

      {/* 11. Merge Ingredient Dialog */}
      <Dialog open={mergeIngOpen} onClose={() => setMergeIngOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MergeTypeIcon color="secondary" /> Merge Ingredient: "{ingToMerge}"
          </DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Merge all occurrences of <strong>"{ingToMerge}"</strong> into another ingredient.
                  All recipes using this ingredient will keep their exact amounts and units, but will be updated to use the target ingredient name. 
                  <strong>"{ingToMerge}"</strong> will then be removed from the master autocomplete dictionary.
              </DialogContentText>

              {(() => {
                const affected = recipes.filter(r => 
                  r.ingredients && Array.isArray(r.ingredients) && 
                  r.ingredients.some(ing => ing.ingredient_name && ing.ingredient_name.trim().toLowerCase() === (ingToMerge || '').trim().toLowerCase())
                );
                return (
                  <Alert severity={affected.length > 0 ? "info" : "warning"} sx={{ mb: 3 }}>
                      <strong>Recipes Affected:</strong> Currently found in <strong>{affected.length}</strong> recipe(s)
                      {affected.length > 0 && (
                          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, fontSize: '0.85rem' }}>
                              {affected.map(r => (
                                  <li key={r.id}>{r.recipe_name || r.title || 'Untitled'}</li>
                              ))}
                          </Box>
                      )}
                  </Alert>
                );
              })()}

              <Autocomplete
                  freeSolo
                  options={dictionaryIngredients.filter(i => i.toLowerCase() !== (ingToMerge || '').toLowerCase())}
                  value={targetIng}
                  onChange={(e, val) => setTargetIng(val || '')}
                  onInputChange={(e, val) => setTargetIng(val)}
                  renderInput={(params) => (
                      <TextField 
                          {...params} 
                          label="Merge Into Target Ingredient..." 
                          placeholder="e.g. All-Purpose Flour" 
                          fullWidth 
                          autoFocus 
                          helperText="Select an existing ingredient from the dictionary or type a new standardized name"
                      />
                  )}
              />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setMergeIngOpen(false)} disabled={isMergingIng}>
                  Cancel
              </Button>
              <Button 
                  onClick={handleMergeIngredientSubmit} 
                  variant="contained" 
                  color="primary" 
                  disabled={!targetIng.trim() || isMergingIng}
                  startIcon={isMergingIng ? <CircularProgress size={18} color="inherit" /> : <MergeTypeIcon />}
              >
                  {isMergingIng ? "Merging..." : "Confirm Merge"}
              </Button>
          </DialogActions>
      </Dialog>

      {/* 12. Merge Cuisine Dialog */}
      <Dialog open={mergeCuisineOpen} onClose={() => setMergeCuisineOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MergeTypeIcon color="secondary" /> Merge Cuisine: "{cuisineToMerge}"
          </DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                  Merge all recipes currently categorized as <strong>"{cuisineToMerge}"</strong> into another cuisine style.
                  <strong>"{cuisineToMerge}"</strong> will then be removed from the standard cuisine dropdown.
              </DialogContentText>

              {(() => {
                const affected = recipes.filter(r => 
                  r.cuisine && r.cuisine.trim().toLowerCase() === (cuisineToMerge || '').trim().toLowerCase()
                );
                return (
                  <Alert severity={affected.length > 0 ? "info" : "warning"} sx={{ mb: 3 }}>
                      <strong>Recipes Affected:</strong> Currently found in <strong>{affected.length}</strong> recipe(s)
                      {affected.length > 0 && (
                          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, fontSize: '0.85rem' }}>
                              {affected.map(r => (
                                  <li key={r.id}>{r.recipe_name || r.title || 'Untitled'}</li>
                              ))}
                          </Box>
                      )}
                  </Alert>
                );
              })()}

              <Autocomplete
                  freeSolo
                  options={dictionaryCuisines.filter(c => c.toLowerCase() !== (cuisineToMerge || '').toLowerCase())}
                  value={targetCuisine}
                  onChange={(e, val) => setTargetCuisine(val || '')}
                  onInputChange={(e, val) => setTargetCuisine(val)}
                  renderInput={(params) => (
                      <TextField 
                          {...params} 
                          label="Merge Into Target Cuisine..." 
                          placeholder="e.g. American" 
                          fullWidth 
                          autoFocus 
                          helperText="Select an existing cuisine or type a new standardized style"
                      />
                  )}
              />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setMergeCuisineOpen(false)} disabled={isMergingCuisine}>
                  Cancel
              </Button>
              <Button 
                  onClick={handleMergeCuisineSubmit} 
                  variant="contained" 
                  color="secondary" 
                  disabled={!targetCuisine.trim() || isMergingCuisine}
                  startIcon={isMergingCuisine ? <CircularProgress size={18} color="inherit" /> : <MergeTypeIcon />}
              >
                  {isMergingCuisine ? "Merging..." : "Confirm Merge"}
              </Button>
          </DialogActions>
      </Dialog>

      {/* --- SNACKBAR NOTIFICATION --- */}
      <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
          <Alert 
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
              severity={snackbar.severity} 
              variant="filled"
              sx={{ width: '100%' }}
          >
              {snackbar.message}
          </Alert>
      </Snackbar>

    </Box>
  );
};

export default AdminDashboard;